import type { PropsWithChildren, RefObject } from 'react';
import { useCallback } from 'react';

import type { Table } from '@tanstack/react-table';

import type { SearchingProps } from './use-api-controller';

import { DeleteButton } from '../../buttons';
import { DebouncedInput } from '../../inputs';
import { DataTableViewOptions } from '../view-options';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  searching: SearchingProps;
  isDeleting?: boolean;
  deleteCallback?: (ids: number[]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Opt-in: links search to table keyboard nav (`useDataTableToolbarKeyboardNav`). */
  searchInputRef?: RefObject<HTMLInputElement | null>;
  rightSlot?: React.ReactNode;
  /** Rendered in the right toolbar section immediately before View options. */
  preViewSlot?: React.ReactNode;
  hideTableViewOptions?: boolean;
  /** When true, the search input syncs with external value changes (e.g., for programmatic clear). */
  synchSearchWithExternal?: boolean;
}

export function DataTableToolbar<TData>({
  table,
  children,
  searching,
  isDeleting,
  rightSlot,
  preViewSlot,
  hideTableViewOptions = false,
  onKeyDown,
  searchInputRef,
  deleteCallback,
  synchSearchWithExternal = false,
}: PropsWithChildren<DataTableToolbarProps<TData>>) {
  const handleBulkDelete = useCallback(() => {
    if (deleteCallback) {
      deleteCallback(table.getFilteredSelectedRowModel().rows.map((row) => (row.original as { id: number }).id));
    }
  }, [deleteCallback, table]);

  return (
    <header className="flex flex-col items-end justify-between gap-x-2 gap-y-4 sm:flex-row">
      <DebouncedInput
        containerClassName="sm:w-72"
        value={searching.search_str}
        onChange={(v) => searching.setSearch(`${v}`)}
        synchExternalValue={synchSearchWithExternal}
        onKeyDown={onKeyDown}
        inputRef={searchInputRef}
        focused
      />

      <nav className="flex w-full flex-1 justify-between gap-2">
        {children || <section />}

        <section className="flex gap-2">
          {rightSlot}

          {deleteCallback && table.getFilteredSelectedRowModel().rows.length > 0 && (
            <DeleteButton size="sm" callback={handleBulkDelete} confirmDelete hideModel isDeleting={isDeleting} />
          )}

          {!hideTableViewOptions && <DataTableViewOptions table={table} />}
          {preViewSlot}
        </section>
      </nav>
    </header>
  );
}
